import type { KeyboardEvent } from 'react';

import { ContactAvatar } from '@poseidon/components/Avatar';
import { cn } from '@poseidon/components/cn';
import { Button } from '@poseidon/components/Input2';
import { Arrows, General } from '@poseidon/components/StrokeIcons';

import { HoverPeoplePopover } from '~/components/HoverPeoplePopover';
import { faceTone, isManualTaskWaitNode, nodeStatusLabel } from '~/components/Sequence/sequenceSteps';

/** Avatar ring by alert tone — red (errored/dead) or amber (blocked/stuck), each as a border + a tinted
 * background halo; the neutral pile ring otherwise. */
const faceRing = (nodeId: string) => {
  const tone = faceTone(nodeId);
  if (tone === 'error') return 'ring-2 ring-red-500 ring-offset-1 ring-offset-red-200 dark:ring-red-500 dark:ring-offset-red-900';
  if (tone === 'blocked')
    return 'ring-2 ring-amber-500 ring-offset-1 ring-offset-amber-200 dark:ring-amber-500 dark:ring-offset-amber-900';
  return 'ring-2 ring-slate-50 dark:ring-neutral-900';
};

/** Popover-row background (base + hover) by alert tone, or the neutral hover otherwise. */
const rowTint = (nodeId: string, clickable: boolean) => {
  const tone = faceTone(nodeId);
  if (tone === 'error')
    return cn('bg-red-50 dark:bg-red-950/40', clickable && 'cursor-pointer hover:bg-red-100 dark:hover:bg-red-950/70');
  if (tone === 'blocked')
    return cn('bg-amber-50 dark:bg-amber-950/40', clickable && 'cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-950/70');
  return clickable ? 'cursor-pointer hover:bg-lynch-50 dark:hover:bg-slate-800' : undefined;
};

export interface SequenceFace {
  id: string;
  firstName: string | null;
  lastName: string | null;
  imageSrc: string | null;
  /** The FLOW_JOURNEY node this person currently sits on — resolved to a human status in the popover. */
  nodeId: string;
  /** Why they're in this (usually parked/errored) state — the real reason, e.g. the Tavily error. */
  detail: string | null;
}

const faceName = (f: SequenceFace) => [f.firstName, f.lastName].filter(Boolean).join(' ') || 'Unknown';

interface SequenceNodeFacesProps {
  sample: SequenceFace[];
  total: number;
  /** When provided, clicking a person (pile avatar OR popover row) opens their contact detail panel
   * IN PLACE (over the sequence page) — the handler updates the route query, it does not navigate away. */
  onOpenContact?: (face: SequenceFace) => void;
  /** When provided, each person row shows a control that restarts their journey (re-run from the top). */
  onRestart?: (face: SequenceFace) => void;
  /** When provided, each person row in the popover shows a control that unenrolls them from the sequence. */
  onRemove?: (face: SequenceFace) => void;
  /** When provided, a person parked on a call/reminder to-do ({@link isManualTaskWaitNode}) shows a "Mark
   * done" control that completes the to-do and advances them past the manual step. */
  onCompleteTask?: (face: SequenceFace) => void;
}

/**
 * The people currently sitting on a node: a small overlapping avatar pile + total count. Hovering the pile
 * opens a popover listing each person with the stage they're parked on, the concrete reason (when stuck —
 * e.g. the real web-search error), and (when `onRemove` is set) a control to remove them. Clicking a person
 * — a pile avatar OR a popover row — opens that contact's detail panel over the sequence page (via
 * `onOpenContact`, which updates the route query rather than navigating). The pile avatars carry NO tooltip
 * — the popover is the single source of per-person detail (avoids a double-tooltip). `total` is exact;
 * `sample` is a capped subset, so removal/open is only offered for the sampled people shown.
 */
export function SequenceNodeFaces({
  sample,
  total,
  onOpenContact,
  onRestart,
  onRemove,
  onCompleteTask,
}: SequenceNodeFacesProps) {
  if (total === 0) return null;
  const shown = sample.slice(0, 3);
  const pileOverflow = total - shown.length;

  // A11y-friendly clickable props for a non-button element that opens the contact panel (omitted entirely
  // when there's no handler, so we never render a dead/no-op clickable element).
  const openProps = (f: SequenceFace) =>
    onOpenContact == null
      ? {}
      : {
          role: 'button' as const,
          tabIndex: 0,
          onClick: () => onOpenContact(f),
          onKeyDown: (e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpenContact(f);
            }
          },
        };

  return (
    <HoverPeoplePopover
      triggerClassName="flex items-center"
      panelClassName="w-72"
      panelTestId="sequence-node-faces-panel"
      trigger={
        <>
          {shown.map((f, i) => (
            <div
              key={f.id}
              {...openProps(f)}
              aria-label={onOpenContact != null ? `Open ${faceName(f)}` : undefined}
              className={cn(
                'relative inline-flex rounded-full',
                onOpenContact != null && 'cursor-pointer transition-transform hover:z-10 hover:-translate-y-0.5',
                i > 0 && '-ml-2',
              )}>
              <ContactAvatar
                firstName={f.firstName}
                lastName={f.lastName}
                imageSrc={f.imageSrc}
                shape="circle"
                className={cn('size-6', faceRing(f.nodeId))}
              />
            </div>
          ))}
          {pileOverflow > 0 && (
            <div className="-ml-2 flex size-6 items-center justify-center rounded-full bg-lynch-100 text-[10px] font-semibold text-lynch-600 ring-2 ring-slate-50 dark:bg-slate-700 dark:text-lynch-200 dark:ring-neutral-900">
              +{pileOverflow}
            </div>
          )}
        </>
      }>
      {/* One row per sampled person — avatar + name + stage + reason + (optional) restart/remove control. */}
      <div className="mb-1.5 text-[11px] font-semibold text-lynch-500 dark:text-lynch-300">
        {total} {total === 1 ? 'person' : 'people'} here
      </div>
      <div className="flex flex-col gap-1">
        {sample.map((f) => (
          <div key={f.id} className="flex items-center gap-1">
            <div
              {...openProps(f)}
              className={cn(
                'group/row flex min-w-0 flex-1 items-center gap-2 rounded-md p-0.5',
                rowTint(f.nodeId, onOpenContact != null),
              )}>
              <ContactAvatar
                firstName={f.firstName}
                lastName={f.lastName}
                imageSrc={f.imageSrc}
                shape="circle"
                className={cn('size-6 shrink-0 self-start', faceRing(f.nodeId))}
              />
              <div className="min-w-0 flex-1">
                <div
                  className={cn(
                    'truncate text-xs font-medium text-lynch-700 dark:text-lynch-100',
                    onOpenContact != null && 'group-hover/row:underline',
                  )}>
                  {faceName(f)}
                </div>
                <div className="truncate text-[10px] text-lynch-400 dark:text-lynch-400">{nodeStatusLabel(f.nodeId)}</div>
                {f.detail != null && (
                  // `overflow-wrap:anywhere` (not `break-words`) so a long unbroken error token still
                  // wraps inside the fixed-width panel instead of spilling past it.
                  <div className="mt-0.5 text-[10px] leading-tight text-rose-500 [overflow-wrap:anywhere] dark:text-rose-400">
                    {f.detail}
                  </div>
                )}
              </div>
            </div>
            {onCompleteTask != null && isManualTaskWaitNode(f.nodeId) && (
              <Button
                size="2xs"
                color="green"
                tone="soft"
                icon={<General.CheckCircle />}
                tooltip="Mark done — complete this to-do and advance the sequence"
                onClick={() => onCompleteTask(f)}
              />
            )}
            {onRestart != null && (
              <Button
                size="2xs"
                color="blue"
                tone="soft"
                icon={<Arrows.RefreshCcw05 />}
                tooltip="Restart journey (re-run from the top)"
                onClick={() => onRestart(f)}
              />
            )}
            {onRemove != null && (
              <Button
                size="2xs"
                color="red"
                tone="soft"
                icon={<General.Trash01 />}
                tooltip="Remove from sequence"
                onClick={() => onRemove(f)}
              />
            )}
          </div>
        ))}
        {total > sample.length && (
          <div className="pl-8 text-[10px] font-medium text-lynch-400">+{total - sample.length} more</div>
        )}
      </div>
    </HoverPeoplePopover>
  );
}
